'use client';

import { useState } from 'react';
import type { Matcher } from '@daypicker/react';
import { format, isValid, parse, parseISO, startOfDay, subYears } from 'date-fns';
import { useFormContext } from 'react-hook-form';
import type { FieldValues, PathValue } from 'react-hook-form';

import type { FormDatePickerFieldStoryblok } from '@utils/storyblok/storyblok';
import CalendarSvg from '@vectors/calendar.svg';
import { Button } from '@common/interaction/Button';
import { Calendar } from '@common/interaction/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@common/interaction/Popover';

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from './Form';

type FormDatePickerFieldProps<T extends FieldValues> = Omit<
  FormDatePickerFieldStoryblok,
  '_uid'
> & {
  defaultValue?: PathValue<T, any>;
  disabled?: boolean;
  errorMessage?: string;
};

function toDateValue(value: unknown) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = /^\d{4}-\d{2}-\d{2}/.test(value)
      ? parseISO(value)
      : parse(value, 'dd-MM-yyyy', new Date());

    return isValid(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
}

export function FormDatePickerField<T extends FieldValues>({
  defaultValue,
  disabled,
  disableFuture,
  disablePast,
  errorMessage,
  helperText,
  label,
  minEighteen,
  name,
  required,
}: FormDatePickerFieldProps<T>) {
  const { control } = useFormContext<T>();
  const [open, setOpen] = useState(false);
  const today = startOfDay(new Date());
  const minimumDate = disablePast ? today : undefined;
  const maximumDate = minEighteen
    ? startOfDay(subYears(today, 18))
    : disableFuture
      ? today
      : undefined;
  const disabledDates: Matcher[] = [
    ...(minimumDate ? [{ before: minimumDate }] : []),
    ...(maximumDate ? [{ after: maximumDate }] : []),
  ];
  const defaultMonth = minimumDate ?? maximumDate ?? today;

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field }) => {
        const selectedDate = toDateValue(field.value);

        return (
          <FormItem>
            <FormLabel>
              {label} {required ? '*' : ''}
            </FormLabel>
            <Popover
              onOpenChange={setOpen}
              open={open}
            >
              <FormControl>
                <PopoverTrigger asChild>
                  <Button
                    aria-label={
                      selectedDate
                        ? `${label ?? 'Datum'}: ${format(selectedDate, 'dd-MM-yyyy')}`
                        : `${label ?? 'Datum'} kiezen`
                    }
                    aria-required={required}
                    className="h-12 w-full justify-between rounded-md border border-border-input bg-light px-3 py-2 text-left font-sans text-base font-normal text-primary shadow-md shadow-black/5 ring-1 ring-black/10 hover:bg-light hover:text-primary hover:shadow-md focus-visible:ring-primary/50"
                    color="primary"
                    disabled={disabled}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    type="button"
                    variant="ghost"
                  >
                    <span className={selectedDate ? undefined : 'text-primary/55'}>
                      {selectedDate ? format(selectedDate, 'dd-MM-yyyy') : 'dd-mm-jjjj'}
                    </span>
                    <CalendarSvg data-icon="inline-end" />
                  </Button>
                </PopoverTrigger>
              </FormControl>
              <PopoverContent
                align="start"
                className="max-h-[var(--radix-popover-content-available-height)] w-auto max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-primary/15 bg-light p-3 text-primary shadow-soft-lg"
                collisionPadding={16}
                sideOffset={8}
                sticky="always"
              >
                <Calendar
                  autoFocus
                  captionLayout="dropdown"
                  defaultMonth={selectedDate ?? defaultMonth}
                  disabled={disabledDates}
                  endMonth={maximumDate ?? new Date(2100, 11, 31)}
                  mode="single"
                  navLayout="around"
                  onSelect={(date) => {
                    field.onChange(date ?? '');

                    if (date) {
                      setOpen(false);
                    }
                  }}
                  selected={selectedDate}
                  startMonth={minimumDate ?? new Date(1900, 0, 1)}
                />
              </PopoverContent>
            </Popover>
            {helperText && <FormDescription>{helperText}</FormDescription>}
            {errorMessage && <FormMessage>{errorMessage}</FormMessage>}
          </FormItem>
        );
      }}
    />
  );
}
